import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Edge,
  type Node,
} from '@xyflow/react'
import { LayoutGrid, Plus, Trash2, Users } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { findRule, isCatchAllRule, BRANCH_PRESETS, type Mailbox, type RoutingRule } from '@/lib/email-rules'
import {
  applySavedPositions,
  buildRoutingFlowGraph,
  clearSavedLayout,
  layoutRoutingGraph,
  loadSavedPositions,
  ruleIdFromNodeId,
  saveNodePosition,
  type FlowNodeData,
} from '@/lib/routing-flow-graph'
import { flowNodeTypes } from '@/components/routing-flow/flow-nodes'
import { RuleEditorSheet, type EditorMode } from '@/components/routing-flow/RuleEditorSheet'
import { DirectoryPanel } from '@/components/routing-flow/DirectoryPanel'
import { cn } from '@/lib/utils'

interface RoutingFlowCanvasProps {
  artistId: string
  mailboxFilter: 'all' | 'global' | string
  mailboxes: Mailbox[]
  rules: RoutingRule[]
  onRefresh: () => void
  showDirectory: boolean
  onToggleDirectory: () => void
}

function FlowCanvasInner({
  artistId,
  mailboxFilter,
  mailboxes,
  rules,
  onRefresh,
  showDirectory,
  onToggleDirectory,
}: RoutingFlowCanvasProps) {
  const { getIntersectingNodes } = useReactFlow()
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<FlowNodeData>>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])
  const [editor, setEditor] = useState<EditorMode | null>(null)
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null)
  const [showPresets, setShowPresets] = useState(false)

  const mailboxKey = mailboxFilter

  const rebuild = useCallback(
    (useAutoLayout: boolean) => {
      const graph = buildRoutingFlowGraph({ mailboxes, rules, mailboxFilter })
      let positioned = graph.nodes
      const saved = loadSavedPositions(artistId, mailboxKey)
      if (useAutoLayout || Object.keys(saved).length === 0) {
        positioned = layoutRoutingGraph(graph.nodes, graph.edges)
      } else {
        positioned = applySavedPositions(graph.nodes, saved)
      }
      setNodes(positioned)
      setEdges(graph.edges)
    },
    [artistId, mailboxFilter, mailboxKey, mailboxes, rules, setEdges, setNodes],
  )

  useEffect(() => {
    rebuild(false)
  }, [rebuild])

  const selectedRule = useMemo(
    () => (selectedRuleId ? findRule(rules, selectedRuleId) ?? null : null),
    [rules, selectedRuleId],
  )

  const onNodeClick = useCallback((_: unknown, node: Node<FlowNodeData>) => {
    const id = ruleIdFromNodeId(node.id)
    if (id) setSelectedRuleId(id)
  }, [])

  const onNodeDragStop = useCallback(
    async (_: unknown, node: Node<FlowNodeData>) => {
      if (node.type === 'feed' || node.type === 'aiIngestor') return

      saveNodePosition(artistId, mailboxKey, node.id, node.position)

      if (node.type !== 'subFilter' && node.type !== 'guard') return

      const ruleId = ruleIdFromNodeId(node.id)
      if (!ruleId) return

      const targets = getIntersectingNodes(node).filter(
        (n) => n.type === 'branch' || n.type === 'subFilter',
      )
      const target = targets.find((t) => t.id !== node.id)
      if (!target) return

      const newParentId = ruleIdFromNodeId(target.id)
      if (!newParentId) return

      const current = findRule(rules, ruleId)
      if (!current || current.parentRuleId === newParentId) return

      try {
        await artistFetch(`/email-rules/${ruleId}`, {
          method: 'PATCH',
          body: { parentRuleId: newParentId },
        })
        onRefresh()
      } catch {
        rebuild(false)
      }
    },
    [artistId, getIntersectingNodes, mailboxKey, onRefresh, rebuild, rules],
  )

  async function deleteSelected() {
    if (!selectedRuleId) return
    if (!window.confirm('Delete this routing rule and its sub-filters?')) return
    try {
      await artistFetch(`/email-rules/${selectedRuleId}`, { method: 'DELETE' })
      setSelectedRuleId(null)
      onRefresh()
    } catch (err) {
      window.alert(err instanceof ApiClientError ? err.message : 'Delete failed')
    }
  }

  async function toggleSelected() {
    if (!selectedRule) return
    try {
      await artistFetch(`/email-rules/${selectedRule.id}`, {
        method: 'PATCH',
        body: { enabled: !selectedRule.enabled },
      })
      onRefresh()
    } catch (err) {
      window.alert(err instanceof ApiClientError ? err.message : 'Update failed')
    }
  }

  async function quickAddPreset(preset: (typeof BRANCH_PRESETS)[number]) {
    setShowPresets(false)
    try {
      await artistFetch('/email-rules', {
        method: 'POST',
        body: {
          name: preset.name,
          conditions: preset.conditions,
          action: 'forward_to_role',
          targetRole: preset.role ?? 'tour_manager',
          ...(preset.department ? { targetDepartment: preset.department } : {}),
          priority: 100,
          mailboxId: mailboxFilter === 'all' || mailboxFilter === 'global' ? null : mailboxFilter,
        },
      })
      onRefresh()
    } catch (err) {
      window.alert(err instanceof ApiClientError ? err.message : 'Could not add branch')
    }
  }

  const newRuleMailboxId =
    mailboxFilter === 'all' || mailboxFilter === 'global' ? null : mailboxFilter

  return (
    <div className="relative h-[min(72vh,820px)] min-h-[480px] overflow-hidden rounded-xl border border-ovation-ink/10 bg-gradient-to-b from-slate-50 to-white shadow-inner">
      <div className="absolute left-4 top-4 z-10 flex flex-wrap items-center gap-2">
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowPresets((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-ovation-ink px-3 py-2 text-xs font-medium text-ovation-paper shadow-md hover:bg-ovation-ink/90"
          >
            <Plus className="h-3.5 w-3.5" />
            Add branch
          </button>
          {showPresets && (
            <div className="absolute left-0 top-full z-20 mt-2 w-56 rounded-lg border border-ovation-ink/10 bg-white p-2 shadow-xl">
              {BRANCH_PRESETS.map((p) => (
                <button
                  key={p.name}
                  type="button"
                  onClick={() => quickAddPreset(p)}
                  className="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-indigo-50"
                >
                  {p.name}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  setShowPresets(false)
                  setEditor({ type: 'create-branch', parentRuleId: null, mailboxId: newRuleMailboxId })
                }}
                className="mt-1 block w-full rounded-md border border-dashed border-indigo-200 px-3 py-2 text-left text-sm text-indigo-700 hover:bg-indigo-50"
              >
                Custom branch…
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => {
            clearSavedLayout(artistId, mailboxKey)
            rebuild(true)
          }}
          className="inline-flex items-center gap-1.5 rounded-lg border border-ovation-ink/15 bg-white px-3 py-2 text-xs font-medium shadow-sm hover:bg-ovation-ink/5"
        >
          <LayoutGrid className="h-3.5 w-3.5" />
          Auto-layout
        </button>

        <button
          type="button"
          onClick={onToggleDirectory}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium shadow-sm',
            showDirectory
              ? 'border-indigo-500 bg-indigo-50 text-indigo-800'
              : 'border-ovation-ink/15 bg-white hover:bg-ovation-ink/5',
          )}
        >
          <Users className="h-3.5 w-3.5" />
          People &amp; departments
        </button>
      </div>

      {selectedRule && (
        <div className="absolute right-4 top-4 z-10 flex max-w-xs flex-col gap-2 rounded-lg border border-ovation-ink/10 bg-white/95 p-3 shadow-lg backdrop-blur-sm">
          <div className="text-xs font-semibold text-ovation-ink">{selectedRule.name}</div>
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setEditor({ type: 'edit', rule: selectedRule })}
              className="rounded-md bg-indigo-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-indigo-700"
            >
              Edit
            </button>
            {(selectedRule.parentRuleId === null || !isCatchAllRule(selectedRule)) && (
              <button
                type="button"
                onClick={() =>
                  setEditor({
                    type: 'create-subfilter',
                    parentRuleId: selectedRule.id,
                    mailboxId: selectedRule.mailboxId,
                  })
                }
                className="rounded-md border border-indigo-200 px-2.5 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-50"
              >
                + Sub-filter
              </button>
            )}
            <button
              type="button"
              onClick={toggleSelected}
              className="rounded-md border border-ovation-ink/15 px-2.5 py-1 text-[11px] hover:bg-ovation-ink/5"
            >
              {selectedRule.enabled ? 'Disable' : 'Enable'}
            </button>
            <button
              type="button"
              onClick={deleteSelected}
              className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2.5 py-1 text-[11px] text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-3 w-3" />
              Delete
            </button>
          </div>
          <p className="text-[10px] text-ovation-muted">
            Drag sub-filters onto another branch to reparent them.
          </p>
        </div>
      )}

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onNodeDragStop={onNodeDragStop}
        nodeTypes={flowNodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.35}
        maxZoom={1.4}
        nodesDraggable
        nodesConnectable={false}
        elementsSelectable
        proOptions={{ hideAttribution: true }}
        className={cn('routing-flow-canvas')}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#e2e8f0" />
        <Controls showInteractive={false} className="!rounded-lg !border-ovation-ink/10 !shadow-md" />
        <MiniMap
          nodeStrokeWidth={2}
          zoomable
          pannable
          className="!rounded-lg !border !border-ovation-ink/10 !bg-white/90"
        />
      </ReactFlow>

      {editor && (
        <RuleEditorSheet
          mode={editor}
          mailboxes={mailboxes}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null)
            onRefresh()
          }}
        />
      )}

      <DirectoryPanel open={showDirectory} onClose={onToggleDirectory} />
    </div>
  )
}

export function RoutingFlowCanvas(props: RoutingFlowCanvasProps) {
  return (
    <ReactFlowProvider>
      <FlowCanvasInner {...props} />
    </ReactFlowProvider>
  )
}
